import { useCallback, useEffect, useState } from "react";

import { IHttpRequestConfig } from "@/browser";
import { useCancelTokenSource } from "@/hooks/http";

type TCodelistFetchFn<Option, SearchParams> = (query: SearchParams, options?: IHttpRequestConfig) => Promise<Option[]>;

export interface IUseFetchOptionsParams<Option, SearchParams> {
  isDisabled?: boolean;
  fetchData: TCodelistFetchFn<Option, SearchParams>;
  filterAfterFetch?: (option: Option[]) => Option[];
  searchParams?: SearchParams;
}

export function useFetchOptions<Option, SearchParams = {}>(params: IUseFetchOptionsParams<Option, SearchParams>) {
  const { isDisabled, fetchData, filterAfterFetch, searchParams } = params;
  const { refreshToken, source } = useCancelTokenSource();
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOptions = useCallback(async () => {
    setIsLoading(true);
    refreshToken();

    try {
      const response = await fetchData(searchParams || ({} as SearchParams), { cancelToken: source.current.token });
      const filtered = filterAfterFetch ? filterAfterFetch(response) : response;

      setOptions(filtered);
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, filterAfterFetch, searchParams]);

  useEffect(() => {
    if (!isDisabled) {
      fetchOptions();
    }
  }, [isDisabled, fetchOptions]);

  return { options, isLoading };
}
